import { format } from 'pretty-format'

import { UserFriendlyError } from './user-friendly-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/NotFoundError')

/**
 * Some item was requested, but that item does not appear to exist
 */
export class NotFoundError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'NotFoundError'
  public override readonly name: string = NotFoundError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  constructor(
    { id, location, type = 'item' }: NotFoundError.constructor.Options = {},
    actualError?: Error,
  ) {
    super(
      `Could not find ${type}${id !== undefined ? ` ${format(id)}` : ''}${
        location !== undefined ? ` in ${location}` : ''
      }`,
      actualError,
    )
  }
}
export namespace NotFoundError {
  export namespace constructor {
    export interface Options {
      /**
       * What type of thing we were looking for
       *
       * @default 'item'
       */
      type?: string
      /**
       * The specific item we were looking for
       *
       * @default not specified
       */
      id?: string | number
      /**
       * The location that was searched to find this item
       *
       * @default not specified
       */
      location?: string
    }
  }

  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isNotFoundError(val: unknown): val is NotFoundError {
    return !!(val as NotFoundError)?.[MARKER_SYMBOL]
  }
}
